<template>
  <b-table
    :id="`${productType}Table`"
    bordered
    :ref="`${productType}sTable-${budgetYear.year}`"
    head-variant="light"
    :fields="fields"
    :items="getProductsFromListItems(budgetYear.projectBudgetLineItems).sort(sortListItems)"
    class="list-item-table"
    show-empty>
    <template v-slot:cell(product)="row">
      {{ getProduct(row.item) }}
    </template>
    <template v-slot:cell(priceListId)="row">
      {{ getPriceList(row.item) }}
    </template>
    <template v-slot:cell(totalCost)="row">
      {{ getTotalCost(row.item) | toCurrency }}
    </template>
    <template v-slot:cell(fundingSource)="row">
      {{ getFundingSource(row.item) }}
    </template>
    <template v-slot:cell(costShare)="row">
      <b-link
        v-if="isYearEditable(budgetYear)"
        @click="decreasePercent(row)"
        :disabled="row.item.costShare === 0">
        <font-awesome-icon icon="minus-circle" fixed-width />
      </b-link>
      <span class="valueForIncrements">
        {{ row.item.costShare }}%
      </span>
      <b-link
        v-if="isYearEditable(budgetYear)"
        @click="increasePercent(row)"
        :disabled='row.item.costShare === 100'>
        <font-awesome-icon icon="plus-circle" fixed-width />
      </b-link>
    </template>
    <template v-slot:cell(costShareAmt)="row">
      {{ getCostShareAmt(row.item) | toCurrency }}
    </template>
    <template v-slot:cell(actions)="row">
      <b-link
        v-if="isYearEditable(budgetYear)"
        @click="editLineItem(row)"
        class="action-button"
      >
        <font-awesome-icon icon="pen" fixed-width />
      </b-link>
      <b-link
        v-if="isYearEditable(budgetYear)"
        variant="danger"
        @click="deleteLineItem(row)"
        class="action-button">
        <font-awesome-icon class="text-danger" icon="trash" fixed-width />
      </b-link>
    </template>
    <template v-slot:cell(contingencyPercent)="row">
      <b-link
        v-if="isYearEditable(budgetYear)"
        @click="decreaseContingencyPercent(row)"
        :disabled="row.item.contingencyPercent === 0">
        <font-awesome-icon icon="minus-circle" fixed-width />
      </b-link>
      <span class="valueForIncrements">
        {{ row.item.contingencyPercent }}%
      </span>
      <b-link
        v-if="isYearEditable(budgetYear)"
        @click="increaseContingencyPercent(row)"
        :disabled='row.item.contingencyPercent === 100'>
        <font-awesome-icon icon="plus-circle" fixed-width />
      </b-link>
    </template>
  </b-table>
</template>
<script>
import { mapState } from 'vuex';
import ProjectApprovalMembershipManager from '@/mixins/projectApprovalMembershipManager';

export default {
  name: 'ProductTable',
  mixins: [ProjectApprovalMembershipManager],
  props: {
    budgetYear: Object,
    productType: String,
    getProductsFromListItems: Function,
    getFundingSource: Function,
    editLineItem: Function,
    deleteLineItem: Function,
    getTotalCost: Function,
    getCostShareAmt: Function,
  },
  data() {
    return {
      fields: [
        {
          key: 'product',
          label: 'Cost Item',
          class: 'cost-column',
        },
        {
          key: 'priceListId',
          label: 'Price List',
          class: 'price-column',
        },
        {
          key: 'targetDate',
          label: 'Target Date',
          formatter: (value) => {
            let newVal = value;
            if (typeof value === 'string') {
              newVal = new Date(value);
            }
            return newVal ? newVal.toLocaleDateString('en-US', { type: 'short' }) : null;
          },
          class: 'date-column',
        },
        {
          key: 'quantity',
          label: 'QTY',
          formatter: (value) => {
            if (!value) {
              return 0;
            }
            return value;
          },
          class: 'text-right number-column',
        },
        {
          key: 'units',
          label: 'Units',
          formatter: (value) => {
            if (!value) {
              return '';
            }
            return value;
          },
          class: 'number-column',
        },
        {
          key: 'unitCost',
          label: 'Unit Cost',
          formatter: (value) => {
            if (!value) {
              return this.$options.filters.toCurrency(0.0);
            }
            return this.$options.filters.toCurrency(value);
          },
          class: 'text-right currency-column',
        },
        {
          key: 'totalCost',
          label: 'Total Cost',
          class: 'text-right currency-column',
        },
        {
          key: 'costShare',
          label: '% District or Partner Cost',
          class: 'costsharepercent-text-column',
        },
        {
          key: 'costShareAmt',
          label: 'District or Partner Cost',
          class: 'text-right costshare-text-column',
        },
        {
          key: 'contingencyPercent',
          label: '% Contingency',
          class: 'contingencyPercent-column',
        },
        {
          key: 'fundingSource',
          label: 'Funding Source',
          class: 'text-column',
        },
        {
          key: 'notes',
          label: 'Notes',
          class: 'text-column',
        },
        {
          key: 'actions',
          label: '',
          class: 'action-column',
        },
      ],
    };
  },
  computed: {
    ...mapState('Project', {
      priceLists: (state) => state.priceLists,
      products: (state) => state.products,
    }),
  },
  methods: {
    decreasePercent(row) {
      /* eslint-disable-next-line */
      row.item.costShare -= 1;
      this.$store.dispatch('Project/saveProjectBudgetLineItem', row.item);
    },
    increasePercent(row) {
      /* eslint-disable-next-line */
      row.item.costShare += 1;
      this.$store.dispatch('Project/saveProjectBudgetLineItem', row.item);
    },
    decreaseContingencyPercent(row) {
      /* eslint-disable-next-line */
      row.item.contingencyPercent -= 1;
      this.$store.dispatch('Project/saveProjectBudgetLineItem', row.item);
    },
    increaseContingencyPercent(row) {
      /* eslint-disable-next-line */
      row.item.contingencyPercent += 1;
      this.$store.dispatch('Project/saveProjectBudgetLineItem', row.item);
    },
    sortListItems(a, b) {
      // note the setHours is just to zero out the time component in the date objects
      const aDate = new Date(a.targetDate);
      aDate.setHours(0, 0, 0, 0);
      const bDate = new Date(b.targetDate);
      bDate.setHours(0, 0, 0, 0);

      if (aDate.valueOf() === bDate.valueOf()) {
        if (!a.product || !b.product) {
          /* eslint-disable-next-line */
          a.product = this.products.find((pl) => pl.producT_ID === a.costItemId);
          /* eslint-disable-next-line */
          b.product = this.products.find((pl) => pl.producT_ID === b.costItemId);
        }
        if (a.product && b.product) {
          const result = a.product.producT_NAME.localeCompare(b.product.producT_NAME);
          if (result === 0) {
            return a.id - b.id;
          }
          return result;
        }
        return a.costItemId - b.costItemId;
      }
      return aDate.valueOf() - bDate.valueOf();
    },
    getProduct(item) {
      if (item.product) {
        return item.product.producT_NAME;
      }
      const product = this.products.find((pl) => pl.producT_ID === item.costItemId);
      if (product) {
        /* eslint-disable-next-line */
        item.product = product;
        return product.producT_NAME;
      }
      return '';
    },
    getPriceList(item) {
      let priceListName = '';
      const priceList = this.priceLists.find((pl) => pl.pricebooK_ID === item.priceListId);
      if (priceList) {
        /* eslint-disable-next-line */
        item.priceList = priceList;
        priceListName = priceList.name;
      }
      return priceListName;
    },
  },
};
</script>
